'use client';

import { useEffect } from 'react';

/**
 * Dev-only horizontal-overflow detector.
 *
 * Finds every element whose right edge extends past the viewport (or whose
 * left edge falls before it), red-outlines it, and logs a concise, clickable
 * report to the console. This is the proactive half of the app-wide
 * horizontal-overflow guardrail:
 *
 *  - The `.app-overflow-guard { overflow-x: clip }` wrapper (app/layout.tsx) is
 *    the *passive* net — it stops a rogue too-wide element from creating a
 *    horizontal page scroll or shoving content off the right edge ("Sell button
 *    off the page").
 *  - This detector is the *active* half — because `clip` hides the symptom,
 *    `document.scrollWidth` no longer grows past `clientWidth`, so overflow
 *    would otherwise go unnoticed. We detect via each element's
 *    `getBoundingClientRect()` (which keeps the real, pre-clip geometry) and
 *    surface the offending component so it's fixed at the source.
 *
 * Mounted ONLY in development (see app/layout.tsx) — it renders nothing and
 * ships no code to production.
 */

const OUTLINE = '2px solid rgba(220,38,38,0.9)'; // red-600
const TOLERANCE_PX = 2;
const MAX_LOGGED = 15;

/**
 * True when some ancestor between `el` and `<body>` is a real horizontal
 * scroll container (`overflow-x: auto | scroll`). Such an element is reachable
 * by scrolling within that region, so extending past the viewport is intended
 * — e.g. a wide data-table inside its own `overflow-x-auto` card, which
 * legitimately positions off-screen columns behind an internal scrollbar. Only
 * `auto`/`scroll` count: `clip` (the `.app-overflow-guard` net) and `hidden`
 * are non-scrollable, so content contained only by those stays reported.
 */
function insideHorizontalScroller(el: Element): boolean {
  let node: Element | null = el;
  while (node && node !== document.body) {
    // Explicit opt-out for INTENTIONAL off-canvas content (e.g. a CSS
    // marquee whose duplicated row is by design wider than the
    // viewport, clipped by its own overflow-hidden wrapper). Scoped to
    // the attribute so overflow-x:hidden wrappers in general still
    // get flagged — hiding a layout bug is exactly what this detector
    // exists to catch.
    if (node.hasAttribute('data-allow-overflow')) return true;
    if (node !== el) {
      const ox = getComputedStyle(node).overflowX;
      if (ox === 'auto' || ox === 'scroll') return true;
    }
    node = node.parentElement;
  }
  return false;
}

function cssPath(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < 4) {
    let part = node.tagName.toLowerCase();
    if (node.id) {
      part += `#${node.id}`;
      parts.unshift(part);
      break;
    }
    const cls = (node.getAttribute('class') || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 3)
      .join('.');
    if (cls) part += `.${cls}`;
    parts.unshift(part);
    node = node.parentElement;
    depth += 1;
  }
  return parts.join(' > ');
}

export function OverflowDetector() {
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const outlined = new Set<HTMLElement>();

    function clear() {
      for (const el of outlined) {
        el.style.outline = '';
        el.style.outlineOffset = '';
      }
      outlined.clear();
    }

    function scan() {
      clear();
      const docW = document.documentElement.clientWidth;
      const offenders: { el: HTMLElement; right: number; width: number }[] = [];

      for (const el of Array.from(
        document.body.querySelectorAll<HTMLElement>('*'),
      )) {
        const tag = el.tagName;
        if (
          tag === 'SCRIPT' ||
          tag === 'STYLE' ||
          tag === 'NEXT-ROUTE-ANNOUNCER'
        )
          continue;
        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) continue;
        if (r.right > docW + TOLERANCE_PX || r.left < -TOLERANCE_PX) {
          // Skip content reachable inside a legitimate horizontal scroller
          // (e.g. a wide data-table in its own overflow-x-auto card) — that's
          // an internal scroll region, not a page-overflow bug.
          if (insideHorizontalScroller(el)) continue;
          offenders.push({
            el,
            right: Math.round(r.right),
            width: Math.round(r.width),
          });
        }
      }

      if (offenders.length === 0) return;

      // Outline everything that overflows; the narrowest offenders are the
      // likely leaf causes (a wide ancestor usually just inherits a wide child).
      for (const { el } of offenders) {
        el.style.outline = OUTLINE;
        el.style.outlineOffset = '-2px';
        outlined.add(el);
      }

      offenders.sort((a, b) => a.width - b.width);

      console.group(
        `%c⇆ Horizontal overflow: ${offenders.length} element(s) extend past the ${docW}px viewport`,
        'color:#dc2626;font-weight:bold',
      );
      console.warn(
        'These are clipped by the `.app-overflow-guard` wrapper. Fix the source so the layout fits. Narrowest first (likely the cause):',
      );
      for (const { el, right, width } of offenders.slice(0, MAX_LOGGED)) {
        console.warn(`  w=${width}px right=${right}px  ${cssPath(el)}`, el);
      }
      if (offenders.length > MAX_LOGGED) {
        console.warn(`  …and ${offenders.length - MAX_LOGGED} more.`);
      }
      console.groupEnd();
    }

    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    function schedule() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(scan);
      }, 400);
    }

    schedule();
    window.addEventListener('resize', schedule);
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      window.removeEventListener('resize', schedule);
      mo.disconnect();
      if (timer) clearTimeout(timer);
      cancelAnimationFrame(raf);
      clear();
    };
  }, []);

  return null;
}
